import React from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import GridTable from "../../table/gridTable";
import Loader from "../../SharedLayouts/Loader";
import ExportCsvTable from "../../Export/ExportCsvTable";
import IpoStatusChip from "./IpoStatusChip";
import { getCurrencySymbol } from "../../../Helper/getCurrencySymbol";

const formatMoney = (currency, value) =>
  value != null
    ? `${getCurrencySymbol(currency)} ${Number(value).toLocaleString()}`
    : "-";

const formatNumber = (value) =>
  value != null ? Number(value).toLocaleString() : "-";

function IposTable() {
  const navigate = useNavigate();
  const allIpos = useSelector((state) => state?.ipos?.allIpos) || [];
  const loading = useSelector((state) => state?.ipos?.getIposPending);

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "symbol",
      headerName: "Symbol",
      width: 110,
      renderCell: (params) => params.row?.stock?.symbol || "-",
    },
    {
      field: "issuer",
      headerName: "Issuer",
      width: 260,
      renderCell: (params) =>
        params.row?.issuer || params.row?.stock?.name || "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => <IpoStatusChip status={params.row?.status} />,
    },
    {
      field: "offer_price",
      headerName: "Offer Price",
      width: 140,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.offer_price),
    },
    {
      field: "total_shares_on_offer",
      headerName: "Shares on Offer",
      width: 150,
      renderCell: (params) => formatNumber(params.row?.total_shares_on_offer),
    },
    {
      field: "shares_remaining",
      headerName: "Remaining",
      width: 130,
      renderCell: (params) =>
        formatNumber(params.row?.shares_remaining),
    },
    {
      field: "admin_fee_pct",
      headerName: "Admin Fee",
      width: 110,
      renderCell: (params) =>
        params.row?.admin_fee_pct != null
          ? `${params.row.admin_fee_pct}%`
          : "-",
    },
    { field: "offer_open_date", headerName: "Opens", width: 120 },
    { field: "offer_close_date", headerName: "Closes", width: 120 },
    { field: "currency", headerName: "Currency", width: 100 },
  ];

  const handleRowClick = (params) => {
    navigate(`/ipos/${params?.row?.id}`);
  };

  const csvHeaders = [
    "ID",
    "Reference",
    "Symbol",
    "Issuer",
    "Status",
    "Offer Price",
    "Shares on Offer",
    "Remaining",
    "Admin Fee %",
    "Opens",
    "Closes",
    "Currency",
  ];

  const csvRows = allIpos.map((row) => [
    row?.id,
    row?.reference,
    row?.stock?.symbol || "-",
    row?.issuer || row?.stock?.name || "-",
    row?.status,
    formatMoney(row?.currency, row?.offer_price),
    row?.total_shares_on_offer ?? "-",
    row?.shares_remaining ?? "-",
    row?.admin_fee_pct ?? "-",
    row?.offer_open_date,
    row?.offer_close_date,
    row?.currency,
  ]);

  if (loading && allIpos?.length < 1) {
    return <Loader />;
  }

  return (
    <Box sx={{ overflowX: "auto", width: "100%", cursor: "pointer" }}>
      <div className="flex justify-end mb-2">
        <ExportCsvTable headers={csvHeaders} rows={csvRows} filename="IPOs.csv" />
      </div>
      <GridTable
        rows={allIpos}
        columns={columns}
        type={"ipos"}
        noReport
        noSearch
        handleRowClick={handleRowClick}
      />
    </Box>
  );
}

export default IposTable;
